import React from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Build } from "@mui/icons-material";
import CustomDataGrid from '../../../../components/ui/customDataGrid/customDataGrid.component';
import EntityReferenceLink from '../../../../components/ui/entityReference/entityReferenceLink.component';
import { buildOrderedPartReferenceId } from '../../../../utils/entityRoutes';
import { USED_PARTS_ENDPOINT } from '../../../../consts';
import styles from './usedParts.module.css';
import { useHasPermission } from '../../../../contexts/auth';
import { formatDateTimeBadge, formatMoney, formatQuantity } from '../../../../utils';
import {
  containsStringFilterOperators,
  equalsNumberFilterOperators,
  exactDateFilterOperators,
} from '../../../../components/ui/customDataGrid/filterOperators';

export const UsedParts = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const canViewUsedParts = useHasPermission("service.view_usedpart");
  const canViewJobs = useHasPermission("service.view_job");
  const canViewShipments = useHasPermission("shipping.view_shipment");
  const canViewOrderedParts = useHasPermission("service.view_orderedpart");

  const columns: GridColDef[] = [
    {
      // Field name matches the backend's dotted UsedPartFilter.job_number
      // filter; valueGetter returns the raw nested number so filtering and
      // sorting operate on it too - previously there was no valueGetter at
      // all, so both silently operated on undefined (row["job__number"]
      // doesn't exist; the display, via renderCell reading row.job_info
      // directly, was the only thing that worked).
      field: "job_number",
      headerName: t("Used for"),
      editable: false,
      filterOperators: containsStringFilterOperators(),
      valueGetter: (value, row) => row.job_info?.number,
      renderCell: (params) =>
        // A part sent by courier left stock in a shipment, not a job.
        params.row.shipment_info ? (
          <EntityReferenceLink
            entityType="shipment"
            entityId={params.row.shipment_info.id}
            label={`${t("Shipment")} ${params.row.shipment_info.waybill || `#${params.row.shipment_info.id}`}`}
            canPreview={canViewShipments}
          />
        ) : (
          <EntityReferenceLink
            entityType="job"
            entityId={params.row.job_info?.id}
            label={params.row.job_info?.number}
            canPreview={canViewJobs}
          />
        ),
    },
    {
      field: "ordered_part_number",
      headerName: t("From Order"),
      editable: false,
      filterOperators: containsStringFilterOperators(),
      valueGetter: (value, row) => row.ordered_part_info?.number,
      renderCell: (params) => {
        const orderedPartInfo = params.row.ordered_part_info;
        return (
          <EntityReferenceLink
            entityType="orderedPart"
            entityId={
              orderedPartInfo
                ? buildOrderedPartReferenceId(orderedPartInfo.part_id, orderedPartInfo.id)
                : undefined
            }
            label={orderedPartInfo?.number}
            canPreview={canViewOrderedParts}
          />
        );
      },
    },
    {
      field: "quantity",
      headerName: t("Quantity"),
      type: "number",
      filterOperators: equalsNumberFilterOperators(),
      valueFormatter: (value) => formatQuantity(value),
    },
    {
      field: "total",
      headerName: t("Total Cost"),
      type: "number",
      filterOperators: equalsNumberFilterOperators(),
      valueFormatter: (value) => formatMoney(value),
    },
    {
      field: "updated_at",
      headerName: t("Used At"),
      filterOperators: exactDateFilterOperators(),
      renderCell: (params) => formatDateTimeBadge(params.value) || "-",
    },
  ];

  const handleRowClick = (usedPartId: string) => {
    navigate(`/service/stock-status/${id}/used-parts/${usedPartId}`);
  };

  if (!canViewUsedParts) {
    return null;
  }

  // Filter used parts by part ID through ordered_part relationship
  const usedPartsUrl = id ? `${USED_PARTS_ENDPOINT}?part=${id}` : USED_PARTS_ENDPOINT;

  return (
    <Box className={styles.container}>
      <CustomDataGrid
        icon={<Build />}
        title={t('Used Parts')}
        url={usedPartsUrl}
        gridSettingsKey="USED_PARTS"
        columns={columns}
        refreshEventName={["usedPartAdded", "usedPartUpdated"]}
        onRowClick={handleRowClick}
      />
    </Box>
  );
};
